import React, { RefObject, useRef, useState } from 'react';
import styled from 'styled-components';
import { THEME_PRIMARY, themeColor } from '../../styles/basics/colors';
import MaterialIcon from '../common/MaterialIcon';
import { MATERIAL_ICON_CAMPAIGNS } from '../common/Button/Icon';
import { useTranslation } from 'react-i18next';
import { PreOrderStockDetails } from '../../store/api';
import { sortCollection } from '../utils/sortCollection';
import { LENDING_MODEL_OCOU, LENDING_MODEL_SIM, UNLIMITED_USAGE_LIMIT } from '../../store/constants';
import { Popup, PopupTheme } from '../components/Popup/Popup';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { LoanFormatState } from '../../app-store/loanFormat/loanFormatReducer';

const StCampaignLabel = styled.b`
    color: ${themeColor(THEME_PRIMARY)};
`;

const StMaterialIcon = styled(MaterialIcon)`
    font-size: 12px;
    line-height: unset;
    vertical-align: top;
    color: #f07e32;
`;

const StSpan = styled.span`
    color: #f07e32;
    font-weight: bold;
`;

type Props = {
    readonly loanFormat: LoanFormatState;
    readonly preOrderStockDetails?: PreOrderStockDetails;
    readonly showLabels?: boolean;
    readonly isCampaignProduct?: boolean;
};

export const mapPreOrderStockDetailsData = (stockDetails: PreOrderStockDetails): PreOrderStockDetails => {
    const combinedStockDetails = {};
    Object.entries(stockDetails).forEach(stockDetailItem => {
        const [spocId, { usageConditions }] = stockDetailItem;

        combinedStockDetails[spocId] = {
            spocName: stockDetails[spocId].spocName,
            collectionName: stockDetails[spocId].collectionName,
            usageConditions: {}
        };

        Object.entries(usageConditions).forEach(([usageCondition, stockAmount]) => {
            const parsedLendingModelKey = usageCondition.split('-');
            const muc = parsedLendingModelKey[0];
            const usageLimit = parseInt(parsedLendingModelKey[1]);

            const label = `${muc}-${usageLimit}`;

            if (combinedStockDetails[spocId].usageConditions.hasOwnProperty(label)) {
                combinedStockDetails[spocId].usageConditions[label] += stockAmount;
            } else {
                combinedStockDetails[spocId].usageConditions[label] = stockAmount;
            }
        });
    });
    return combinedStockDetails;
};

function PreOrderStockDetailsList({
    loanFormat,
    preOrderStockDetails,
    showLabels = false,
    isCampaignProduct = false
}: Props) {
    const { t } = useTranslation();
    const toggleRef = useRef<HTMLElement>(null);
    const [showStockSplit, setShowStockSplit] = useState(false);

    const icon = isCampaignProduct ? MATERIAL_ICON_CAMPAIGNS : 'content_copy';

    if (!preOrderStockDetails) {
        return (
            <li title={t('components.preOrderStock.title')}>
                <StMaterialIcon icon={icon} />
                <StSpan> {showLabels ? t('components.preOrderStock.preOrderLabelwithoutStock') : '0'}</StSpan>
            </li>
        );
    }

    const preOrderStockAmount = Object.values(preOrderStockDetails)
        .flatMap(stockDetailItem => Object.values(stockDetailItem.usageConditions))
        .reduce((acc, val) => acc + val, 0);

    return (
        <li
            title={t('components.preOrderStock.title')}
            style={{ position: 'relative' }}
            data-testid="preorder-stock"
            ref={toggleRef as RefObject<HTMLLIElement>}
            onMouseEnter={() => setShowStockSplit(true)}
            onMouseLeave={() => setShowStockSplit(false)}
        >
            <StMaterialIcon icon={icon} />{' '}
            <StSpan>
                {showLabels
                    ? t('components.preOrderStock.preOrderLabelwithStock', {
                          preOrderStockAmount
                      })
                    : t('components.preOrderStock.preOrderStockWithoutLabel', {
                          preOrderStockAmount
                      })}
            </StSpan>
            <Popup
                target={toggleRef}
                visible={showStockSplit}
                placement="top-start"
                arrow={true}
                portal={true}
                theme={PopupTheme.CUSTOM}
                popupClassName="connect-popover"
            >
                <div data-testid="preorder-stock-details">
                    {Object.entries(mapPreOrderStockDetailsData(preOrderStockDetails))
                        .map(([spocId, detail]) => ({ spocId, ...detail }))
                        .sort(sortCollection)
                        .map(stockDetails => {
                            const { spocId, spocName, collectionName, usageConditions } = stockDetails;
                            const localisedCollectionName = spocId.startsWith('SITE')
                                ? t('components.stockDetails.individualCollection', {
                                      spocName,
                                      defaultValue: collectionName
                                  })
                                : t('components.stockDetails.sharedCollection', {
                                      spocName,
                                      defaultValue: collectionName
                                  });

                            return (
                                <section className="bordered-section" key={collectionName}>
                                    <span className="title">{localisedCollectionName}</span>
                                    <table className="left-aligned-data">
                                        <tbody>
                                            {Object.keys(usageConditions).map(key => {
                                                const stock = usageConditions[key];
                                                const loanFormatLabel = t(
                                                    `loanFormat.plain.${mapLoanFormat(
                                                        loanFormat.requestKey
                                                    )?.toLowerCase()}`,
                                                    { count: stock }
                                                );
                                                const parsedLendingModelKey = key.split('-');
                                                const lendingModel = parsedLendingModelKey[0];
                                                const usageLimit = parseInt(parsedLendingModelKey[1]);

                                                const isLendingModelSim = lendingModel === LENDING_MODEL_SIM;
                                                const isLendingModelOCOU = lendingModel === LENDING_MODEL_OCOU;

                                                const unlimited =
                                                    isLendingModelSim && usageLimit === UNLIMITED_USAGE_LIMIT;

                                                const labelText = unlimited
                                                    ? t('shop.subscriptions.unlimited')
                                                    : isLendingModelOCOU || isNaN(usageLimit)
                                                    ? t('lendingModel.ocou')
                                                    : `${t(
                                                          `lendingModel.${lendingModel.toLowerCase()}`
                                                      )}-${usageLimit}`;

                                                const preOrderStockLabel = `${loanFormatLabel} - ${labelText}`;
                                                const campaignPreOrderStockLabel = t(
                                                    `campaignStock.${loanFormat.name.toLowerCase()}`,
                                                    {
                                                        count: stock
                                                    }
                                                );

                                                return isCampaignProduct ? (
                                                    <tr key={`${spocId}_${key}`} className="mb-0">
                                                        <td>
                                                            <span>{stock}</span>
                                                        </td>
                                                        <td>
                                                            <StCampaignLabel>
                                                                <b>{campaignPreOrderStockLabel}</b>
                                                            </StCampaignLabel>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={`${spocId}_${key}`}>
                                                        <td>
                                                            <span>{stock}</span>
                                                        </td>
                                                        <td>
                                                            <b>{preOrderStockLabel}</b>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </section>
                            );
                        })}
                </div>
            </Popup>
        </li>
    );
}

export default PreOrderStockDetailsList;
